import { isDefined } from "@clipboard-health/util-ts";
import { type UseGetFacilityProfileResponse } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useUpdateFacilityProfile } from "@src/appV2/FacilityProfile/api/useUpdateFacilityProfile";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useSupportArticles } from "@src/appV2/HelpCenter/api/useSupportArticles";
import { findSupportArticleUrlById } from "@src/appV2/HelpCenter/utils";
import { useToast } from "@src/appV2/lib";
import { useModalState } from "@src/lib/ui-react";
import { useState } from "react";

import { useCloseOpenNegotiations } from "../../api/useCloseOpenNegotiations";
import { type SettingsPermissions } from "../../types";
import { MapleIntegrationConfirmationDialog } from "./MapleIntegrationConfirmationDialog";
import { RateBillingSectionView } from "./RateBillingSectionView";
import { useTu } from "@src/appV2/i18n/useTu";

const RATE_NEGOTIATION_ON = "ON";
const RATE_NEGOTIATION_OFF = "OFF";
const RATE_NEGOTIATION_ARTICLE_ID = "pay-rate-negotiations";

interface RateBillingSectionContainerProps {
  facilityProfile: UseGetFacilityProfileResponse | undefined;
  guardSettingsAction: (action: () => void) => void;
  permissions: SettingsPermissions;
  workplaceId: string;
  onSettingsChanged: () => void;
}

export function RateBillingSectionContainer({
  facilityProfile,
  guardSettingsAction,
  permissions,
  workplaceId,
  onSettingsChanged,
}: RateBillingSectionContainerProps) {
  const { tu } = useTu("settings");
  const { showSuccessToast, showErrorToast } = useToast();

  const mapleIntegrationFlag = useCbhFlag(CbhFeatureFlag.MAPLE_INTEGRATION, {
    defaultValue: { allApisThroughBgJobs: false, enableApi: false, enableWebhook: false },
  });

  const isMapleIntegrationVisible =
    mapleIntegrationFlag.enableWebhook || mapleIntegrationFlag.enableApi;

  const hasVerificationPreference = Boolean(facilityProfile?.verificationPreference);

  const { data: supportArticlesData } = useSupportArticles();
  const rateNegotiationArticleUrl = findSupportArticleUrlById(
    supportArticlesData,
    RATE_NEGOTIATION_ARTICLE_ID
  );

  const [rateNegotiationEnabled, setRateNegotiationEnabled] = useState(
    facilityProfile?.rateNegotiation === RATE_NEGOTIATION_ON
  );
  const [mapleEnabled, setMapleEnabled] = useState(
    facilityProfile?.mapleIntegrationEnabled ?? false
  );
  const [pendingMapleValue, setPendingMapleValue] = useState<boolean | undefined>(undefined);
  const mapleConfirmationModalState = useModalState();

  const { isLoading: isFacilityProfileUpdating, mutateAsync: updateFacilityProfile } =
    useUpdateFacilityProfile();
  const { isLoading: isClosingNegotiations, mutateAsync: closeNegotiations } =
    useCloseOpenNegotiations();

  const isUpdating = isFacilityProfileUpdating || isClosingNegotiations;

  const handleRateNegotiationToggle = async (checked: boolean) => {
    setRateNegotiationEnabled(checked);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        rateNegotiation: checked ? RATE_NEGOTIATION_ON : RATE_NEGOTIATION_OFF,
      });

      if (!checked) {
        const result = await closeNegotiations({ facilityId: workplaceId });
        showSuccessToast(`Closed ${result.countOfNegotiationsClosed ?? 0} negotiations`);
      }

      showSuccessToast(tu("Rate negotiation setting updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating rate negotiation setting. Please try again later."));
      setRateNegotiationEnabled(!checked);
    }
  };

  const handleMapleIntegrationToggleRequested = (checked: boolean) => {
    if (!hasVerificationPreference) {
      return;
    }

    setPendingMapleValue(checked);
    mapleConfirmationModalState.openModal();
  };

  const handleConfirmMapleIntegrationToggle = async () => {
    const checked = pendingMapleValue;
    if (!isDefined(checked)) {
      return;
    }

    setMapleEnabled(checked);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        mapleIntegrationEnabled: checked,
      });
      showSuccessToast(tu("Maple integration updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating Maple integration. Please try again later."));
      setMapleEnabled(!checked);
    } finally {
      mapleConfirmationModalState.closeModal();
      setPendingMapleValue(undefined);
    }
  };

  return (
    <>
      <RateBillingSectionView
        isMapleIntegrationVisible={isMapleIntegrationVisible}
        isRateNegotiationVisible
        isUpdating={isUpdating}
        mapleEnabled={mapleEnabled}
        permissions={permissions}
        rateNegotiationArticleUrl={rateNegotiationArticleUrl}
        rateNegotiationEnabled={rateNegotiationEnabled}
        onMapleIntegrationToggle={(checked) => {
          guardSettingsAction(() => {
            handleMapleIntegrationToggleRequested(checked);
          });
        }}
        onRateNegotiationToggle={(checked) => {
          guardSettingsAction(() => {
            void handleRateNegotiationToggle(checked);
          });
        }}
      />
      <MapleIntegrationConfirmationDialog
        modalState={mapleConfirmationModalState}
        isTurningOn={pendingMapleValue ?? false}
        isLoading={isFacilityProfileUpdating}
        onConfirm={() => {
          void handleConfirmMapleIntegrationToggle();
        }}
        onCancel={() => {
          mapleConfirmationModalState.closeModal();
          setPendingMapleValue(undefined);
        }}
      />
    </>
  );
}
